import type {
  AllowedContexts,
  ContextItem,
} from '~/store/profile/profileSlice';

export const validateAllowedContexts = ({
  allowedContexts,
  actingPermissionsContextIds,
}: {
  allowedContexts?: AllowedContexts;
  actingPermissionsContextIds?: {
    organizationsId?: string;
    hubsId?: string;
    fleetsId?: string;
  };
}) => {
  if (!allowedContexts) {
    return false;
  }

  const { organizationsId, hubsId, fleetsId } =
    actingPermissionsContextIds || {};

  if (!organizationsId) {
    // no scope to validate with
    return true;
  }

  const organization = allowedContexts[organizationsId];
  if (!organization) {
    return false;
  }

  if (!hubsId) {
    return true;
  }

  const hub = organization.hubs?.[hubsId];
  if (!hub) {
    return false;
  }

  if (!fleetsId) {
    return true;
  }

  return !!hub.fleets?.[fleetsId];
};

/**
 * @param allowedContexts raw org context hierarchy
 * @param actingPermissionsContextIds acting user's assigned org contexts, used to set an appropriate root context
 * @returns appropriate root context based on acting user's assigned org contexts, as well as any available sub-contexts
 */
export const transformAllowedContexts = ({
  allowedContexts,
  actingPermissionsContextIds,
}: {
  allowedContexts?: AllowedContexts;
  actingPermissionsContextIds?: {
    organizationsId?: string;
    hubsId?: string;
    fleetsId?: string;
  };
}): { root: ContextItem; subContexts: ContextItem[] } | undefined => {
  if (!allowedContexts || Object.keys(allowedContexts).length === 0) {
    return undefined;
  }

  const rootId = Object.keys(allowedContexts)[0];
  const rootContext = allowedContexts[rootId];

  if (!rootContext) {
    return undefined;
  }

  const hubContexts = rootContext.hubs || {};

  // if the user is fleet-scoped, return the fleet context
  if (actingPermissionsContextIds?.fleetsId) {
    const fleetId = actingPermissionsContextIds.fleetsId;
    const fleetHub = Object.values(hubContexts).find((hubContext) => {
      const fleetContexts = hubContext.fleets || {};
      return Object.hasOwn(fleetContexts, fleetId);
    });
    if (!fleetHub) {
      console.error(
        `Allowed contexts do not contain fleet-scoped user's fleet`
      );
      return undefined;
    }
    const fleetContext: ContextItem = {
      ...fleetHub.fleets[fleetId],
      parentIds: [rootId, fleetHub.id],
    };
    return { root: fleetContext, subContexts: [] };
  }

  const subContexts: ContextItem[] = Object.entries(hubContexts)
    .flatMap(([hubId, hubContext]) => {
      const fleetContexts = hubContext.fleets || {};
      return Object.values(fleetContexts).map((fleet) => ({
        ...fleet,
        parentIds: [rootId, hubId],
      }));
    })
    .sort((first, second) => first.name.localeCompare(second.name));

  return {
    root: { id: rootContext.id, name: rootContext.name },
    subContexts,
  };
};
